import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../components/StatusBadge';
import { fetchAnalysisResult } from '../lib/api';
import { format } from 'date-fns';

const SEVERITY_ORDER: Record<string, number> = { critical: 3, warning: 2, ok: 1, info: 0 };

function severityWeight(s: string): number {
  return SEVERITY_ORDER[s] ?? 0;
}

export default function AnalysisDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState<'impact' | 'severity' | 'host'>('impact');
  const { t } = useTranslation();

  useEffect(() => {
    if (id) {
      fetchAnalysisResult(Number(id))
        .then(setData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  const findings: any[] = useMemo(() => {
    try { return JSON.parse(data?.findings || '[]'); } catch { return []; }
  }, [data]);

  const details: any = useMemo(() => {
    try { const d = JSON.parse(data?.details || 'null'); return d; } catch { return data?.details || null; }
  }, [data]);

  // Aggregate findings by description
  const aggregated = useMemo(() => {
    const map: Record<string, { description: string; severity: string; hosts: string[]; count: number; details: string[] }> = {};
    findings.forEach((f: any) => {
      const desc = f.description || 'Unknown';
      if (!map[desc]) {
        map[desc] = { description: desc, severity: f.severity || 'info', hosts: [], count: 0, details: [] };
      }
      map[desc].count++;
      if (f.host && !map[desc].hosts.includes(f.host)) map[desc].hosts.push(f.host);
      if (f.details && !map[desc].details.includes(f.details)) map[desc].details.push(f.details);
      // keep highest severity
      if (severityWeight(f.severity) > severityWeight(map[desc].severity)) map[desc].severity = f.severity;
    });
    const groups = Object.values(map);
    switch (sortMode) {
      case 'severity':
        return groups.sort((a, b) => severityWeight(b.severity) - severityWeight(a.severity) || b.count - a.count);
      case 'host':
        return groups.sort((a, b) => b.hosts.length - a.hosts.length || b.count - a.count);
      default: // impact = count * severity
        return groups.sort((a, b) => (b.count * severityWeight(b.severity)) - (a.count * severityWeight(a.severity)));
    }
  }, [findings, sortMode]);

  const totalHosts = useMemo(() => {
    const set = new Set<string>();
    findings.forEach((f: any) => { if (f.host) set.add(f.host); });
    return set.size || 1;
  }, [findings]);

  if (loading) return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  if (!data) return <p className="text-gray-500 dark:text-gray-400">{t('analysis.notFound')}</p>;

  const sortOptions = [
    { value: 'impact' as const, label: t('analysis.sortImpact') },
    { value: 'severity' as const, label: t('analysis.sortSeverity') },
    { value: 'host' as const, label: t('analysis.sortHost') },
  ];

  return (
    <div className="space-y-6">
      <Link to="/analysis" className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
        <ArrowLeft className="w-4 h-4" /> {t('analysis.backToAnalysis')}
      </Link>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 capitalize">
              {data.analyzer?.replace(/_/g, ' ')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('analysis.tenant')}: {data.tenant_id} &middot;{' '}
              {data.created_at ? format(new Date(data.created_at), 'PPpp') : '-'}
            </p>
          </div>
          <StatusBadge status={data.status} size="md" />
        </div>
        <p className="mt-4 text-gray-700 dark:text-gray-300">{data.summary}</p>
        {data.score != null && (
          <p className="mt-2 text-lg font-bold text-gray-900 dark:text-gray-100">{t('analysis.score')}: {data.score.toFixed(1)}%</p>
        )}
      </div>

      {/* Aggregated Findings */}
      {findings.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              {t('analysis.findings')} ({findings.length})
              {aggregated.length < findings.length && (
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
                  {aggregated.length} {t('analysis.uniqueFindings')}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-1">
              <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortMode(opt.value)}
                  className={`text-xs px-2 py-1 rounded-md transition-colors ${
                    sortMode === opt.value
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {aggregated.map((g, i) => {
              const impactPct = Math.round((g.hosts.length / totalHosts) * 100);
              return (
                <div key={i} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusBadge status={g.severity} />
                      {g.count > 1 && (
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 text-xs font-bold rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {g.count}x
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {g.hosts.length > 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {g.hosts.length} {g.hosts.length === 1 ? 'host' : 'hosts'} ({impactPct}%)
                        </span>
                      )}
                      {impactPct >= 50 && (
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                          {t('analysis.highImpact')}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{g.description}</p>
                  {g.hosts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {g.hosts.slice(0, 5).map((h) => (
                        <span key={h} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded font-mono">{h}</span>
                      ))}
                      {g.hosts.length > 5 && <span className="text-xs text-gray-400">+{g.hosts.length - 5}</span>}
                    </div>
                  )}
                  {g.details.length > 0 && (
                    <div className="mt-1">
                      {g.details.slice(0, 2).map((d, j) => (
                        <p key={j} className="text-xs text-gray-500 dark:text-gray-400">{d}</p>
                      ))}
                      {g.details.length > 2 && <p className="text-xs text-gray-400">+{g.details.length - 2} more</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Raw details */}
      {details && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('analysis.details')}</h3>
          </div>
          <pre className="p-5 text-xs text-gray-600 dark:text-gray-300 overflow-auto max-h-96 bg-white dark:bg-gray-900/50">
            {typeof details === 'string' ? details : JSON.stringify(details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
